// Single source of truth for the shared `bao_settings` localStorage object.
// Individual preferences (move hints, language, …) merge into this one object
// rather than using separate keys. All reads/writes are defensive so that
// private mode / quota / corrupt JSON degrade gracefully instead of throwing.

export const BAO_SETTINGS_KEY = "bao_settings";

// Concrete shape (rather than Record<string, unknown>) so the compiler rejects
// unknown-key typos at the write sites.
export type StoredSettings = {
  showHints?: boolean;
  language?: string;
};

export function readSettingsObject(): StoredSettings | null {
  try {
    const raw = localStorage.getItem(BAO_SETTINGS_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    return parsed as StoredSettings;
  } catch {
    // private mode / quota / corrupt JSON — degrade to "no settings"
    return null;
  }
}

/**
 * Atomically merges multiple keys in a single read-modify-write, so co-located
 * updates (e.g. a future "save all settings" handler) can't clobber each other.
 */
export function writeSettings(partial: StoredSettings): void {
  try {
    const existing = readSettingsObject() ?? {};
    localStorage.setItem(BAO_SETTINGS_KEY, JSON.stringify({ ...existing, ...partial }));
  } catch {
    // private mode / quota — preference simply won't persist this session
  }
}

/** Merges a single, type-checked key into the persisted settings object. */
export function writeSetting<K extends keyof StoredSettings>(key: K, value: StoredSettings[K]): void {
  writeSettings({ [key]: value } as Pick<StoredSettings, K>);
}
